import { useMutation } from '@apollo/client';

import { EDUCATION_LEVEL_OPTIONS, type EDUCATION_LEVEL_OPTIONS_TYPE } from 'bundles/onboarding-common/constants';
import LearnerObjectUpdateEducationProfile from 'bundles/onboarding-common/queries/LearnerObjectUpdateEducationProfile.graphql';
import type {
  LearnerObject_UpdateEducationProfileMutation as LearnerObjectUpdateEducationProfileMutationType,
  LearnerObject_UpdateEducationProfileMutationVariables as LearnerObjectUpdateEducationProfileMutationVariables,
} from 'bundles/onboarding-common/queries/__generated__/LearnerObjectUpdateEducationProfile';

export const EDUCATION_LEVEL_MAP: Record<
  EDUCATION_LEVEL_OPTIONS_TYPE,
  NonNullable<LearnerObjectUpdateEducationProfileMutationVariables['input']['educationProfile']['educationLevel']>
> = {
  [EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_LESS_THAN_HIGH_SCHOOL_DIPLOMA]: 'LESS_THAN_HIGH_SCHOOL_DIPLOMA',
  [EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_HIGH_SCHOOL_DIPLOMA]: 'HIGH_SCHOOL_DIPLOMA',
  [EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_COLLEGE_NO_DEGREE]: 'COLLEGE_NO_DEGREE',
  [EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_ASSOCIATE_DEGREE]: 'ASSOCIATE_DEGREE',
  [EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_BACHELOR_DEGREE]: 'BACHELOR_DEGREE',
  [EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_MASTERS_DEGREE]: 'MASTERS_DEGREE',
  [EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_PROFESSIONAL_DEGREE]: 'PROFESSIONAL_DEGREE',
  [EDUCATION_LEVEL_OPTIONS.EDUCATION_LEVEL_DOCTORATE_DEGREE]: 'DOCTORATE_DEGREE',
};

const useLearnerObjectUpdateEducationProfile = () => {
  const [updateEducationProfileMutation] = useMutation<
    LearnerObjectUpdateEducationProfileMutationType,
    LearnerObjectUpdateEducationProfileMutationVariables
  >(LearnerObjectUpdateEducationProfile, {
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
  });

  const updateEducationProfile = async (educationLevel: EDUCATION_LEVEL_OPTIONS_TYPE) => {
    const { errors, data } = await updateEducationProfileMutation({
      variables: {
        input: {
          educationProfile: {
            educationLevel: EDUCATION_LEVEL_MAP[educationLevel],
          },
        },
      },
    });

    if (errors) {
      return { success: false, errors: errors.map((error) => error.message) };
    }

    if (data?.LearnerObject_UpdateEducationProfile.__typename === 'LearnerObject_UpdateEducationProfileResultError') {
      return { success: false, errors: [data.LearnerObject_UpdateEducationProfile.message] };
    }

    return { success: true, errors: [] };
  };

  return { updateEducationProfile };
};

export default useLearnerObjectUpdateEducationProfile;
